import { useQuery } from '@tanstack/react-query';

import type { SigninRequirements, SignoutRequirements } from '@sine/sine-core-api-types';
import { useNotification } from '@sine/sine-ui-components';

import parseServerError from '../../helpers/parseServerError';
import { QueryKeys } from '../../query.client';
import visitService, { VISIT_EVENT_TYPE } from '../../services/visit/visit.service';

type UseSiteRequirementsQueryCommonReturnType = {
  isLoadingSiteRequirements: boolean;
  isFetchingSiteRequirements: boolean;
  refetchSiteRequirements: () => void;
};

type UseSiteRequirementsQuerySigninReturn = ReturnType<typeof useQuery<SigninRequirements>> &
  UseSiteRequirementsQueryCommonReturnType & {
    siteRequirements: SigninRequirements | undefined;
  };

type UseSiteRequirementsQuerySignoutReturn = ReturnType<typeof useQuery<SignoutRequirements>> &
  UseSiteRequirementsQueryCommonReturnType & {
    siteRequirements: SignoutRequirements | undefined;
  };

type UseSiteRequirementsQueryReturn = UseSiteRequirementsQueryCommonReturnType & {
  siteRequirements: SigninRequirements | SignoutRequirements | undefined;
};

export function useSiteRequirementsQuery(
  event: (typeof VISIT_EVENT_TYPE)['SIGN_IN'],
  siteId?: string,
): UseSiteRequirementsQuerySigninReturn;
export function useSiteRequirementsQuery(
  event: (typeof VISIT_EVENT_TYPE)['SIGN_OUT'],
  siteId?: string,
): UseSiteRequirementsQuerySignoutReturn;
export function useSiteRequirementsQuery(
  event: (typeof VISIT_EVENT_TYPE)['SIGN_IN'] | (typeof VISIT_EVENT_TYPE)['SIGN_OUT'],
  siteId?: string,
): UseSiteRequirementsQueryReturn {
  const { addNotification } = useNotification();

  const {
    data: siteRequirements,
    isLoading: isLoadingSiteRequirements,
    isFetching: isFetchingSiteRequirements,
    refetch: refetchSiteRequirements,
  } = useQuery({
    enabled: !!siteId,
    queryKey: [QueryKeys.SITE_REQUIREMENTS, event, siteId],
    queryFn: async () => {
      if (event === VISIT_EVENT_TYPE.SIGN_IN) {
        return visitService.requirements(siteId!, VISIT_EVENT_TYPE.SIGN_IN);
      }

      return visitService.requirements(siteId!, VISIT_EVENT_TYPE.SIGN_OUT);
    },
    onError: (error: any) => {
      addNotification({
        message: parseServerError(error),
        variant: 'danger',
      });
    },
  });

  return {
    siteRequirements,
    isLoadingSiteRequirements,
    isFetchingSiteRequirements,
    refetchSiteRequirements,
  };
}
